/**
 * legalVersions — the ONE place each published policy's effective date lives.
 *
 * WHY THIS EXISTS (2026-09-11). None of the three policy pages rendered a date
 * at all, while their own copy referred to one that was not there:
 *   Privacy.tsx  — "update the effective date above"      (there was no date above)
 *   Cookie.tsx   — "with a refreshed date stamp"          (there was no date stamp)
 * A privacy policy has to identify when it took effect — CalOPPA (Cal. B&P
 * §22575(b)(4)-(5)) says so outright — and a reader cannot tell whether a term
 * changed if nothing is dated. Three hand-typed dates would drift the way the
 * trial terms did before src/config/trialTerms.ts centralised those, so there
 * is one module and a gate (scripts/check-legal-effective-date.mjs) that fails
 * the build if a page stops rendering its date.
 *
 * WHEN TO BUMP: any change to the substance a reader relies on — what we
 * collect, who we share it with, what you can ask us to do, what you owe. Not
 * for typos, styling, or a reworded sentence that says the same thing. Bumping
 * is what tells a returning reader something actually changed, so a bump that
 * did not need to happen is its own small dishonesty.
 *
 * FORMAT: ISO `YYYY-MM-DD`, rendered through `formatEffectiveDate` so all three
 * pages read identically. Parsed as UTC on purpose — `new Date('2026-09-11')`
 * is UTC midnight, and formatting it in the viewer's local zone would show the
 * 10th to anyone west of Greenwich.
 */

export interface LegalDocumentVersion {
  /** ISO date the current text took effect. */
  readonly effectiveDate: string;
  /** What changed at that date — shown to nobody, read by whoever edits next. */
  readonly note: string;
}

export const LEGAL_VERSIONS = {
  privacy: {
    effectiveDate: '2026-09-11',
    note:
      'Cookies section corrected (no consent banner, no non-essential cookies, GPC ' +
      'honored); optional-analytics consent basis narrowed to what is actually ' +
      'collected; GDPR/SCC framing scoped to where those laws apply; the ' +
      'data-rights summary now matches what has shipped.',
  },
  terms: {
    effectiveDate: '2026-09-11',
    note: 'Effective date surfaced. Substance unchanged in this revision.',
  },
  cookie: {
    effectiveDate: '2026-09-11',
    note:
      'Rewritten to describe reality: no analytics product, no advertising or ' +
      'social pixel and no A/B allocator; the browser-storage paragraph now ' +
      'describes TLS in transit and Google Cloud at rest, which is what runs; GPC ' +
      'honored. The prior text disclosed a tracking apparatus that did not exist.',
  },
} as const satisfies Record<'privacy' | 'terms' | 'cookie', LegalDocumentVersion>;

export type LegalDocumentKey = keyof typeof LEGAL_VERSIONS;

/** "september 11, 2026" — lowercase house voice, UTC so the date shown is the date recorded. */
export function formatEffectiveDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d))
    .toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    })
    // House voice is lowercase and no CSS transform may force it
    // (owner rule 2026-08-16, pinned by tests/policy/noUppercase.test.ts),
    // so the string is lowercased at the source rather than in styling.
    .toLowerCase();
}

/** The one string every policy page renders. */
export function effectiveDateLabel(key: LegalDocumentKey): string {
  return `effective ${formatEffectiveDate(LEGAL_VERSIONS[key].effectiveDate)}`;
}
